import {
  asObservationId,
  asObservationPerformerResourceId,
  asPatientId,
  asPractitionerId,
  type ObservationId,
} from "@repo/domain";
import { z } from "zod";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientRefSchema } from "./patient.schemas";
import { visitIdSchema } from "./visit.schemas";

/** Coding schema */
const codingSchema = z.object({
  system: z.string().optional(),
  code: z.string().optional(),
  display: z.string().optional(),
});

/** CodeableConcept schema */
const codeableConceptSchema = z.object({
  coding: z.array(codingSchema).optional(),
  text: z.string().optional(),
});

/** Quantity schema */
const quantitySchema = z.object({
  value: z.number().optional(),
  unit: z.string().optional(),
  system: z.string().optional(),
  code: z.string().optional(),
});

/** Observation component schema */
const observationComponentSchema = z.object({
  code: codeableConceptSchema,
  valueQuantity: quantitySchema.optional(),
  valueCodeableConcept: codeableConceptSchema.optional(),
  valueString: z.string().optional(),
  valueBoolean: z.boolean().optional(),
  valueInteger: z.number().int().optional(),
  interpretation: z.array(codeableConceptSchema).optional(),
});

/** Observation status */
const observationStatusSchema = z.enum([
  "registered",
  "preliminary",
  "final",
  "amended",
  "cancelled",
  "entered-in-error",
]);

export const observationIdSchema: z.ZodType<ObservationId, z.ZodTypeDef, string> = z
  .string()
  .min(1)
  .transform(asObservationId);

// === Input Schemas ===

/** Get observation by ID */
export const observationIdInputSchema = z.object({
  observationId: observationIdSchema,
});
export type ObservationIdInput = z.infer<typeof observationIdInputSchema>;

/** Observation.performer allowed FHIR types — narrow to what we actually emit. */
const performerSchema = z.discriminatedUnion("resourceType", [
  z.object({ resourceType: z.literal("Patient"), id: z.string().min(1).transform(asPatientId) }),
  z.object({ resourceType: z.literal("Practitioner"), id: z.string().min(1).transform(asPractitionerId) }),
  ...(["Organization", "CareTeam", "RelatedPerson", "Device"] as const).map((resourceType) =>
    z.object({
      resourceType: z.literal(resourceType),
      id: z.string().min(1).transform(asObservationPerformerResourceId),
    }),
  ),
]);

/** Per-observation input (no shared context) */
const observationInputSchema = z.object({
  id: newEntityIdSchema,
  code: codeableConceptSchema,
  category: z.array(codeableConceptSchema).optional(),
  status: observationStatusSchema.optional(),
  performer: z.array(performerSchema).optional(),
  valueQuantity: quantitySchema.optional(),
  valueCodeableConcept: codeableConceptSchema.optional(),
  valueString: z.string().optional(),
  valueBoolean: z.boolean().optional(),
  valueInteger: z.number().int().optional(),
  component: z.array(observationComponentSchema).optional(),
  hasMember: z.array(observationIdSchema).optional(),
  note: z.string().optional(),
  bodySite: codeableConceptSchema.optional(),
  effectiveDateTime: z.string().optional(),
});

/** Record one or more observations */
export const recordObservationsInputSchema = z.object({
  visitId: visitIdSchema,
  patient: patientRefSchema,
  observations: z.array(observationInputSchema),
});
export type RecordObservationsInput = z.infer<typeof recordObservationsInputSchema>;

/** Update observation content (pre-finalization). Status changes go through finalize/amend/cancel. */
export const updateObservationInputSchema = z.object({
  observationId: observationIdSchema,
  valueQuantity: quantitySchema.optional(),
  valueCodeableConcept: codeableConceptSchema.optional(),
  valueString: z.string().optional(),
  valueBoolean: z.boolean().optional(),
  valueInteger: z.number().int().optional(),
  component: z.array(observationComponentSchema).optional(),
  note: z.string().optional(),
  effectiveDateTime: z.string().optional(),
  bodySite: codeableConceptSchema.optional(),
  interpretation: z.array(codeableConceptSchema).optional(),
});
export type UpdateObservationInput = z.infer<typeof updateObservationInputSchema>;
